import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import { type ReportingComparisonPdfHttpBody } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { DxButtonModule, DxCheckBoxModule, DxLoadPanelModule, DxPopupModule } from 'devextreme-angular';
import { ReportSettingsPopupComponent } from '../../../../components/report-settings-popup/report-settings-popup.component';
import { ReportPdfActionsComponent } from '../../../../components/report-pdf-actions/report-pdf-actions.component';
import { ProductionComparisonPdfService } from '../production-comparison-pdf.service';

export interface ComparisonPdfBlockOption {
  id: string;
  label: string;
}

/** One dataset's blocks, ticked together or one by one. */
export interface ComparisonPdfBlockGroup {
  title: string;
  blocks: ComparisonPdfBlockOption[];
}

/** Everything the PDF request carries except which blocks, which is this preview's to decide. */
export type ComparisonPdfRequestBase = Omit<ReportingComparisonPdfHttpBody, 'blocks'>;

function isSameSelection(left: ReadonlySet<string>, right: ReadonlySet<string> | null): boolean {
  return !!right && left.size === right.size && [...left].every(id => right.has(id));
}

/**
 * The Comparison PDF on screen, the way a bulletin is previewed.
 *
 * The document fills the page and the choice of what goes in it floats beside it, in the same
 * settings panel the bulletin viewer uses — not modal, so a change can be compared against the
 * pages already rendered. Apply rebuilds the document; nothing is saved.
 *
 * It opens over the dashboard rather than in place of it. The dashboard stays mounted behind,
 * with an arrangement that may not be saved anywhere; closing this returns to it exactly as it
 * was, grids and all, without reloading a table.
 */
@Component({
  selector: 'ag-portal-production-comparison-pdf-preview',
  standalone: true,
  imports: [
    CommonModule,
    DxButtonModule,
    DxCheckBoxModule,
    DxLoadPanelModule,
    DxPopupModule,
    ReportSettingsPopupComponent,
    ReportPdfActionsComponent,
  ],
  templateUrl: './production-comparison-pdf-preview.component.html',
  styleUrls: ['./production-comparison-pdf-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductionComparisonPdfPreviewComponent implements OnInit, OnDestroy {
  @Input({ required: true }) title!: string;
  /** The page as it was when the preview opened; it cannot change while this covers it. */
  @Input({ required: true }) request!: ComparisonPdfRequestBase;
  @Input({ required: true }) blockGroups!: ComparisonPdfBlockGroup[];
  /** Without the extension; the same name the workbook takes. */
  @Input({ required: true }) fileName!: string;
  @Output() readonly closed = new EventEmitter<void>();

  private readonly pdfService = inject(ProductionComparisonPdfService);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly selectedIdsSig = signal<ReadonlySet<string>>(new Set());
  /** What the document on screen was built from, which Apply and Discard are judged against. */
  private readonly renderedIdsSig = signal<ReadonlySet<string> | null>(null);
  protected readonly isLoadingSig = signal(false);
  protected readonly errorMessageSig = signal<string | null>(null);
  protected readonly pdfUrlSig = signal<SafeResourceUrl | null>(null);

  protected readonly hasChangesSig = computed(() => !isSameSelection(this.selectedIdsSig(), this.renderedIdsSig()));
  protected readonly canApplySig = computed(
    () => this.selectedIdsSig().size > 0 && this.hasChangesSig() && !this.isLoadingSig(),
  );

  /**
   * Downloaded under the page's own name rather than whatever the viewer's download control would
   * pick, which for a document opened from a blob is the blob's uuid.
   */
  protected pdfBlob: Blob | null = null;
  private objectUrl: string | null = null;
  /** Only the newest request may put its document on screen; see generate. */
  private requestToken = 0;

  ngOnInit(): void {
    this.selectedIdsSig.set(new Set(this.allBlockIds()));
    void this.generate();
  }

  ngOnDestroy(): void {
    this.requestToken += 1;
    this.releaseObjectUrl();
  }

  protected isSelected(blockId: string): boolean {
    return this.selectedIdsSig().has(blockId);
  }

  /** Ticked when every block in the group is, clear when none is, and in between otherwise. */
  protected getGroupValue(group: ComparisonPdfBlockGroup): boolean | null {
    const selected = group.blocks.filter(block => this.isSelected(block.id)).length;

    if (selected === 0) {
      return false;
    }

    return selected === group.blocks.length ? true : null;
  }

  /**
   * Only a click counts. A check box also reports the value it is handed, and the group's value
   * moves whenever one of its blocks does — taken for a click, that would tick or clear the
   * whole group behind the viewer's back.
   */
  protected onGroupValueChanged(
    group: ComparisonPdfBlockGroup,
    event: { value?: boolean | null; event?: unknown },
  ): void {
    if (!event.event) {
      return;
    }

    const next = new Set(this.selectedIdsSig());
    group.blocks.forEach(block => (event.value === true ? next.add(block.id) : next.delete(block.id)));
    this.selectedIdsSig.set(next);
  }

  protected onBlockValueChanged(blockId: string, event: { value?: boolean | null; event?: unknown }): void {
    if (!event.event) {
      return;
    }

    const next = new Set(this.selectedIdsSig());
    if (event.value === true) {
      next.add(blockId);
    } else {
      next.delete(blockId);
    }

    this.selectedIdsSig.set(next);
  }

  protected apply(): void {
    if (this.canApplySig()) {
      void this.generate();
    }
  }

  protected retry(): void {
    void this.generate();
  }

  /** Back to what the document on screen was built from. */
  protected discard(): void {
    const rendered = this.renderedIdsSig();
    if (rendered) {
      this.selectedIdsSig.set(new Set(rendered));
    }
  }

  protected close(): void {
    this.closed.emit();
  }

  private allBlockIds(): string[] {
    return this.blockGroups.flatMap(group => group.blocks.map(block => block.id));
  }

  /**
   * Builds the document for the blocks ticked now.
   *
   * Each request takes a token and only the newest may write: an Apply pressed while an earlier
   * one is still being built must not have the older document land on top of it.
   */
  private async generate(): Promise<void> {
    const selected = this.selectedIdsSig();
    // In the order they print, which is the order the groups list them in.
    const blocks = this.allBlockIds().filter(id => selected.has(id));
    if (!blocks.length) {
      return;
    }

    const token = ++this.requestToken;
    this.isLoadingSig.set(true);
    this.errorMessageSig.set(null);

    try {
      const response = await this.pdfService.render({ ...this.request, blocks });
      if (token !== this.requestToken) {
        return;
      }

      this.showPdf(response.blob);
      this.renderedIdsSig.set(new Set(blocks));
    } catch (error) {
      if (token !== this.requestToken) {
        return;
      }

      console.error('[ProductionComparisonPdfPreview] Failed to build the PDF', error);
      this.errorMessageSig.set('The PDF could not be built. Try again in a moment.');
    } finally {
      if (token === this.requestToken) {
        this.isLoadingSig.set(false);
      }
    }
  }

  private showPdf(blob: Blob): void {
    this.releaseObjectUrl();
    this.pdfBlob = blob;
    this.objectUrl = URL.createObjectURL(blob);
    this.pdfUrlSig.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));
  }

  private releaseObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
